import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Pagination,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import ProjectNavButtons from '../components/ProjectNavButtons';
import PageSearchBar from '../components/PageSearchBar';
import ProjectFiltersDialog from '../components/ProjectFiltersDialog';
import { buildPeSearchPageHref, countPeFilters } from '../utils/searchState';
import { useAppliedFilterChips } from '../hooks/useAppliedFilterChips';
import { usePeFilters } from '../hooks/usePeFilters';
import { usePeSearchDropdown } from '../hooks/usePeSearchDropdown';
import { triggerDownload } from '../components/fileVisuals';
import {
  projectEssentialApi,
  type ProjectVideoItem,
  type VideoCategoryId,
} from '../services/api';
import { logVideoClicked, logVideoNotesDownloaded } from '../services/analytics';

// ---------------------------------------------------------------------------
// Project Videos page
// ---------------------------------------------------------------------------
// YouTube-inspired: a responsive grid of cards, each with a 16:9
// thumbnail and a compact meta row (title + views • downloads • date
// + download button). The thumbnail is the only clickable surface
// that opens the player.
//
// Data is fetched per category from `GET /api/project-essential/videos`
// and cached in a per-tab map so switching tabs doesn't re-hit the
// network. `resi`, `international`, `marketing` and `consumer` are
// wired backend-side; `upcoming` resolves to an empty payload until
// its album is wired.
// ---------------------------------------------------------------------------

// Categories the user can toggle between. Order here is the display
// order in the tab strip.
const VIDEO_CATEGORIES: ReadonlyArray<{ id: VideoCategoryId; label: string }> = [
  { id: 'resi', label: 'Resi Projects' },
  { id: 'upcoming', label: 'Upcoming Projects' },
  { id: 'commercial', label: 'Commercial & Industrial' },
  { id: 'international', label: 'International' },
  { id: 'marketing', label: 'Marketing Direction & Updates' },
  { id: 'consumer', label: 'Consumer Connect' },
] as const;

// Categories that aren't wired backend-side yet. The page services
// these from the module-scoped MOCK_VIDEOS_BY_CATEGORY table below
// so the rail still has something to render while the underlying
// VideoAlbums get sorted out.
const MOCK_CATEGORIES: ReadonlySet<VideoCategoryId> = new Set<VideoCategoryId>();

// Public sample MP4 used as a stand-in stream for the mock rows.
// CORS-friendly and small — good enough for the in-page player while
// the real StreamingLink values land.
const SAMPLE_VIDEO_URL =
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4';

// Mock dataset keyed by category. Shape matches `ProjectVideoItem`
// exactly so the rest of the page is oblivious to whether a row is
// live or mocked. `notesUrl: null` everywhere — mock rows don't get
// a "Download Notes" footer button, mirroring how a real video
// without a VideoAttachment row would render.
const MOCK_VIDEOS_BY_CATEGORY: Partial<Record<VideoCategoryId, ProjectVideoItem[]>> = {
  upcoming: [
    {
      id: 'mock-up-1',
      title: 'VVIP Preview — Upcoming Launches',
      views: 781,
      durationSeconds: 908,
      createdAt: '2026-04-01T00:00:00.000Z',
      thumbnail: null,
      streamUrl: SAMPLE_VIDEO_URL,
      notesUrl: null,
      notesFilename: null,
    },
    {
      id: 'mock-up-2',
      title: 'Launches Recap — April 2026',
      views: 2_106,
      durationSeconds: 1125,
      createdAt: '2026-04-08T00:00:00.000Z',
      thumbnail: null,
      streamUrl: SAMPLE_VIDEO_URL,
      notesUrl: null,
      notesFilename: null,
    },
    {
      id: 'mock-up-3',
      title: 'Sales Briefing — Pipeline Outlook',
      views: 1_528,
      durationSeconds: 1457,
      createdAt: '2026-03-22T00:00:00.000Z',
      thumbnail: null,
      streamUrl: SAMPLE_VIDEO_URL,
      notesUrl: null,
      notesFilename: null,
    },
  ],
  commercial: [
    {
      id: 'mock-ci-1',
      title: 'Commercial Spotlight — CBD Office Trends',
      views: 1_204,
      durationSeconds: 1342,
      createdAt: '2026-05-02T00:00:00.000Z',
      thumbnail: null,
      streamUrl: SAMPLE_VIDEO_URL,
      notesUrl: null,
      notesFilename: null,
    },
    {
      id: 'mock-ci-2',
      title: 'Industrial Briefing — Logistics & Warehousing',
      views: 894,
      durationSeconds: 1820,
      createdAt: '2026-04-18T00:00:00.000Z',
      thumbnail: null,
      streamUrl: SAMPLE_VIDEO_URL,
      notesUrl: null,
      notesFilename: null,
    },
    {
      id: 'mock-ci-3',
      title: 'F&B Shophouse Investment Outlook',
      views: 612,
      durationSeconds: 996,
      createdAt: '2026-04-05T00:00:00.000Z',
      thumbnail: null,
      streamUrl: SAMPLE_VIDEO_URL,
      notesUrl: null,
      notesFilename: null,
    },
  ],
};

// Per-tab cache shape. `items` is `null` while the request is in
// flight or before the tab has been opened; `error` is set when the
// last fetch failed so the UI can surface a retry-friendly message.
interface CategoryState {
  items: ProjectVideoItem[] | null;
  loading: boolean;
  error: string | null;
}

const EMPTY_STATE: CategoryState = { items: null, loading: false, error: null };

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

// "1,234 views" / "12K views" / "1.2M views" — collapses large counts
// the same way YouTube does so the meta row stays compact.
function formatViews(n: number): string {
  if (n < 1_000) return `${n} view${n === 1 ? '' : 's'}`;
  if (n < 1_000_000) {
    const k = n / 1_000;
    const out = k < 10 ? k.toFixed(1).replace(/\.0$/, '') : Math.round(k).toString();
    return `${out}K views`;
  }
  const m = n / 1_000_000;
  const out = m < 10 ? m.toFixed(1).replace(/\.0$/, '') : Math.round(m).toString();
  return `${out}M views`;
}

function formatCreatedDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// Resolve a `Video.StreamingLink` value into a player-ready source.
// `Video.StreamingLink` is free-form `varchar(500)` so the column
// can hold any of:
//   • a direct media URL (.mp4 / .webm / .ogg / .mov / .m4v)
//   • a YouTube watch URL  (https://www.youtube.com/watch?v=<id>)
//   • a YouTube short URL  (https://youtu.be/<id>)
//   • a YouTube embed URL  (https://www.youtube.com/embed/<id>)
//   • a Vimeo URL          (https://vimeo.com/<id>)
//   • some other player URL — fall back to <iframe> so it still renders.
//
// Returns a `kind` discriminant so the player can decide between a
// native `<video>` element (kind: 'media') and an `<iframe>`
// (kind: 'iframe'). `kind: 'none'` is reserved for empty/invalid
// values so the dialog can show a friendly placeholder.
type StreamSource =
  | { kind: 'media'; src: string }
  | { kind: 'iframe'; src: string; title: string }
  | { kind: 'none' };

const DIRECT_MEDIA_EXTS = /\.(mp4|webm|ogg|ogv|mov|m4v)(\?.*)?$/i;

function resolveStreamSource(video: ProjectVideoItem): StreamSource {
  const raw = (video.streamUrl || '').trim();
  if (!raw) return { kind: 'none' };

  // Vimeo always goes through the embed iframe — even for `external`
  // HLS URLs (`player.vimeo.com/external/<id>.m3u8?s=…&oauth2…`).
  // Those signed manifests are scoped to Vimeo's own player and the
  // CDN refuses cross-origin segment requests from arbitrary hosts,
  // so trying to play them with hls.js loads the manifest but stalls
  // on the first .ts segment. We detect Vimeo *before* the generic
  // .m3u8 / .mp4 branches so the iframe path always wins.
  try {
    const u = new URL(raw);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
      // Extract the numeric video ID from whichever URL shape we got:
      //   • vimeo.com/<id>
      //   • player.vimeo.com/video/<id>
      //   • player.vimeo.com/embed/<id>
      //   • player.vimeo.com/external/<id>.m3u8?…
      const segments = u.pathname.split('/').filter(Boolean);
      let vimeoId: string | null = null;
      if (host === 'vimeo.com') {
        vimeoId = segments[0] || null;
      } else if (segments[0] === 'video' || segments[0] === 'embed') {
        vimeoId = segments[1] || null;
      } else if (segments[0] === 'external' && segments[1]) {
        vimeoId = segments[1].replace(/\.m3u8$/i, '');
      }
      if (vimeoId && /^\d+$/.test(vimeoId)) {
        return {
          kind: 'iframe',
          src: `https://player.vimeo.com/video/${encodeURIComponent(vimeoId)}?autoplay=1`,
          title: video.title || 'Video player',
        };
      }
    }
  } catch {
    // Not a parseable URL — fall through to the generic checks below.
  }

  // Direct media file → native <video>.
  if (DIRECT_MEDIA_EXTS.test(raw)) {
    return { kind: 'media', src: raw };
  }

  // YouTube — accept watch URLs (?v=…), shortened (youtu.be/…) and
  // already-prepared embed URLs. Normalise everything to the embed
  // form so the iframe loads the player chrome.
  try {
    const u = new URL(raw);
    const host = u.hostname.replace(/^www\./, '');

    if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (u.pathname === '/watch') {
        const id = u.searchParams.get('v');
        if (id) {
          return {
            kind: 'iframe',
            src: `https://www.youtube.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0`,
            title: video.title || 'Video player',
          };
        }
      }
      if (u.pathname.startsWith('/embed/')) {
        // Already an embed URL — pass through, but ensure autoplay.
        const sep = u.search ? '&' : '?';
        return {
          kind: 'iframe',
          src: `${raw}${sep}autoplay=1&rel=0`,
          title: video.title || 'Video player',
        };
      }
    }

    if (host === 'youtu.be') {
      const id = u.pathname.replace(/^\//, '');
      if (id) {
        return {
          kind: 'iframe',
          src: `https://www.youtube.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0`,
          title: video.title || 'Video player',
        };
      }
    }

    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
      // Vimeo public URLs look like https://vimeo.com/<id>; the embed
      // host is player.vimeo.com/video/<id>. Re-route either form
      // through the embed host so the player iframe loads.
      if (host === 'player.vimeo.com') {
        const sep = u.search ? '&' : '?';
        return {
          kind: 'iframe',
          src: `${raw}${sep}autoplay=1`,
          title: video.title || 'Video player',
        };
      }
      const id = u.pathname.replace(/^\//, '').split('/')[0];
      if (id) {
        return {
          kind: 'iframe',
          src: `https://player.vimeo.com/video/${encodeURIComponent(id)}?autoplay=1`,
          title: video.title || 'Video player',
        };
      }
    }
  } catch {
    // Not a valid URL — fall through to the iframe fallback below.
  }

  // Unknown provider but we have a URL — load it inside an iframe so
  // the dialog still does its best to play the video. Worst case the
  // iframe is blocked by the source's X-Frame-Options and the user
  // sees an empty frame, which is still better than a hard failure.
  return {
    kind: 'iframe',
    src: raw,
    title: video.title || 'Video player',
  };
}

// Seconds → "mm:ss" for sub-hour clips, "h:mm:ss" otherwise. Mirrors
// the YouTube duration badge convention.
function formatDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '';
  const s = Math.round(totalSeconds);
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  if (hours > 0) {
    return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  }
  return `${minutes}:${pad(seconds)}`;
}

// Pick a sensible save-as filename for the "Download Notes" button.
// Preference order:
//   1. `notesFilename` — the human-friendly name from VideoAttachment.Name
//      (already includes the extension when the source row has one).
//   2. The basename of the URL — covers the common case where Name is
//      null but the Path ends in `…/some-file.pdf`.
//   3. A slugified `<title>-notes` fallback so the download still
//      lands with a recognisable name.
function buildNotesFilename(video: ProjectVideoItem): string {
  if (video.notesFilename && video.notesFilename.trim()) {
    return video.notesFilename.trim();
  }
  if (video.notesUrl) {
    try {
      const u = new URL(video.notesUrl);
      const base = u.pathname.split('/').filter(Boolean).pop();
      if (base) return decodeURIComponent(base);
    } catch {
      // Fall through to the slug fallback below.
    }
  }
  const base = (video.title || video.id || 'video').trim();
  const slug = base
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug || video.id}-notes.pdf`;
}

// Cover fallback used when a row has no Thumbnail. Keeps the rail
// from showing a blank box if the portal source happens to be empty.
const VIDEO_COVER_BGS = ['1f3a5f', '24487a', '2c5b8a', '3d6c97', '4f7da6'];
function fallbackThumb(video: ProjectVideoItem): string {
  // Stable colour per id so the card doesn't flash a different bg
  // between renders.
  let hash = 0;
  const id = String(video.id);
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  const bg = VIDEO_COVER_BGS[Math.abs(hash) % VIDEO_COVER_BGS.length];
  const text = encodeURIComponent(video.title || video.id);
  return `https://placehold.co/640x360/${bg}/ffffff/png?text=${text}`;
}

// ---------------------------------------------------------------------------
// VideoCard
// ---------------------------------------------------------------------------

interface VideoCardProps {
  video: ProjectVideoItem;
  onPlay: (video: ProjectVideoItem) => void;
}

function VideoCard({ video, onPlay }: VideoCardProps) {
  const hasNotes = Boolean(video.notesUrl);

  const handleDownloadNotes = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!video.notesUrl) return;
    logVideoNotesDownloaded(video.id);
    triggerDownload(video.notesUrl, buildNotesFilename(video));
  };

  const durationLabel = formatDuration(video.durationSeconds);
  const coverSrc = video.thumbnail || fallbackThumb(video);

  return (
    <Card
      sx={{
        width: '100%',
        // Themed card surface — same token the HighlightCard and
        // PosterCard use, so the Videos rail reads as part of the same
        // product. Soft shadow + rounded corners lift the card off
        // the page background.
        bgcolor: 'var(--color-surface)',
        color: 'var(--color-text)',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: 'var(--color-shadow-card)',
        border: '1.5px solid var(--color-border-strong)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 200ms ease, box-shadow 200ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
        },
      }}
    >
      {/* Thumbnail — the *only* clickable surface that opens the
          player. Rendered as a real <button> so keyboard / screen-
          reader focus behaves correctly. */}
      <Box
        component="button"
        type="button"
        onClick={() => onPlay(video)}
        aria-label={`Play ${video.title}`}
        sx={{
          all: 'unset',
          position: 'relative',
          display: 'block',
          width: '100%',
          aspectRatio: '16 / 9',
          overflow: 'hidden',
          cursor: 'pointer',
          bgcolor: 'var(--color-surface-elevated)',
          // The card itself lifts on hover, so the thumbnail only
          // needs to surface the play overlay — no extra shadow.
          '&:hover .video-play-overlay': {
            opacity: 1,
          },
          '&:focus-visible': {
            outline: '3px solid var(--color-accent)',
            outlineOffset: '-3px',
          },
        }}
      >
        <Box
          component="img"
          src={coverSrc}
          alt=""
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />

        {/* Hover play overlay — centred circular badge that fades in. */}
        <Box
          className="video-play-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.18)',
            opacity: 0,
            transition: 'opacity 180ms ease',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'rgba(0, 0, 0, 0.6)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PlayArrowRoundedIcon sx={{ fontSize: 36 }} />
          </Box>
        </Box>

        {/* Duration badge — bottom-right pill, mirrors YouTube. Hidden
            when the row has no duration so we don't render an empty
            chip. */}
        {durationLabel && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              px: 0.85,
              height: 22,
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: 1,
              bgcolor: 'rgba(0, 0, 0, 0.75)',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.72rem',
              letterSpacing: '0.02em',
            }}
          >
            {durationLabel}
          </Box>
        )}
      </Box>

      {/* Meta row — non-interactive (per the brief, the title is *not*
          clickable). Title on top, view count + date below. */}
      <Box sx={{ p: { xs: 1.25, sm: 1.5 }, pb: hasNotes ? 0 : { xs: 1.25, sm: 1.5 } }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: 'var(--color-text-strong)',
            lineHeight: 1.3,
            // Clamp to two lines like YouTube card titles, then ellipsis.
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
          title={video.title}
        >
          {video.title}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.25,
            color: 'var(--color-text-muted)',
            fontWeight: 500,
          }}
        >
          {formatViews(video.views)}
          {formatCreatedDate(video.createdAt)
            ? ` • ${formatCreatedDate(video.createdAt)}`
            : ''}
        </Typography>
      </Box>

      {/* Footer button — only rendered when the video has an
          attachment in VideoAttachment. Sits flush at the bottom of
          the card so every card with notes shares the same anchor
          line, regardless of how the title clamps. */}
      {hasNotes && (
        <Box
          sx={{
            mt: 'auto',
            px: { xs: 1.25, sm: 1.5 },
            pt: 1,
            pb: { xs: 1.25, sm: 1.5 },
          }}
        >
          <Button
            onClick={handleDownloadNotes}
            startIcon={<FileDownloadIcon />}
            fullWidth
            size="small"
            variant="outlined"
            aria-label={`Download notes for ${video.title}`}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
              color: 'var(--color-accent-strong)',
              borderColor: 'var(--color-border-strong)',
              bgcolor: 'var(--color-surface)',
              '&:hover': {
                bgcolor: 'var(--color-accent-soft)',
                borderColor: 'var(--color-accent)',
              },
            }}
          >
            Download Notes
          </Button>
        </Box>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// VideoPlayer — pure renderer for the active video's stream source.
// ---------------------------------------------------------------------------
// Black 16:9 frame containing whichever element the StreamingLink
// resolves to (native <video>, provider iframe, or a fallback
// message). Used by the inline watch view; the surrounding card /
// layout chrome lives in `VideoWatchView`.
//
// `key={video.id}` should be applied to the wrapping element by the
// caller so React unmounts and remounts the underlying media element
// when the active video changes — without that, switching between
// two YouTube embeds keeps the same iframe and the new src isn't
// reliably picked up.

function VideoPlayer({ video }: { video: ProjectVideoItem }) {
  const source = resolveStreamSource(video);

  // Bootstrap-style "embed-responsive-16by9" wrapper. We deliberately
  // use the classic padding-top hack (height: 0; padding-top: 56.25%)
  // rather than `aspect-ratio` because the latter sometimes computes
  // unexpectedly when the wrapper sits inside a CSS grid track
  // (`minmax(0, 2fr)`), leaving the media at its intrinsic size and
  // centred inside an oversized black box. The padding-top trick has
  // worked identically across every browser since IE9 and is what the
  // existing huttonsgroup.com site uses.
  const responsiveBoxSx = {
    position: 'relative' as const,
    display: 'block',
    width: '100%',
    height: 0,
    paddingTop: '56.25%', // 9 / 16 = 0.5625
    overflow: 'hidden',
    bgcolor: '#000',
  };
  const responsiveItemSx = {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 0,
    display: 'block',
  };

  if (source.kind === 'media') {
    return (
      <Box sx={responsiveBoxSx}>
        <Box
          component="video"
          src={source.src}
          controls
          autoPlay
          playsInline
          sx={responsiveItemSx}
        />
      </Box>
    );
  }

  if (source.kind === 'iframe') {
    return (
      <Box sx={responsiveBoxSx}>
        <Box
          component="iframe"
          src={source.src}
          title={source.title}
          allow="autoplay; encrypted-media; picture-in-picture; accelerometer; gyroscope"
          allowFullScreen
          referrerPolicy="origin"
          sx={responsiveItemSx}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        aspectRatio: '16 / 9',
        bgcolor: '#000',
        color: 'rgba(255, 255, 255, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 3,
      }}
    >
      <Typography variant="body2">
        Streaming source unavailable for this video.
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// RelatedVideoCard — compact horizontal card for the related-videos rail.
// ---------------------------------------------------------------------------
// Mirrors YouTube's sidebar entries but on a themed Card surface so
// the rail reads as a stack of discrete tiles rather than bare rows.
// ~40% width thumbnail with a duration badge, title clamped to two
// lines, view count + uploaded date below. The whole card is one
// button so the click target is large on mobile.

function RelatedVideoCard({
  video,
  onPlay,
}: {
  video: ProjectVideoItem;
  onPlay: (video: ProjectVideoItem) => void;
}) {
  const durationLabel = formatDuration(video.durationSeconds);
  const coverSrc = video.thumbnail || fallbackThumb(video);
  return (
    <Card
      component="button"
      type="button"
      onClick={() => onPlay(video)}
      aria-label={`Play ${video.title}`}
      sx={{
        all: 'unset',
        cursor: 'pointer',
        display: 'grid',
        gridTemplateColumns: '168px 1fr',
        gap: 1.25,
        alignItems: 'flex-start',
        width: '100%',
        boxSizing: 'border-box',
        p: 1,
        bgcolor: 'var(--color-surface)',
        color: 'var(--color-text)',
        borderRadius: 2,
        border: '1.5px solid var(--color-border-strong)',
        boxShadow: 'var(--color-shadow-card)',
        transition: 'transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
        '&:hover': {
          bgcolor: 'var(--color-surface-elevated)',
          transform: 'translateY(-1px)',
          boxShadow: '0 6px 18px rgba(0, 0, 0, 0.14)',
        },
        '&:focus-visible': {
          outline: '2px solid var(--color-accent)',
          outlineOffset: 2,
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '16 / 9',
          bgcolor: 'var(--color-surface-elevated)',
          borderRadius: 1.25,
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={coverSrc}
          alt=""
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
        {durationLabel && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 4,
              right: 4,
              px: 0.6,
              height: 18,
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: 0.75,
              bgcolor: 'rgba(0, 0, 0, 0.78)',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.65rem',
              letterSpacing: '0.02em',
            }}
          >
            {durationLabel}
          </Box>
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: 'var(--color-text-strong)',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
          title={video.title}
        >
          {video.title}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.25,
            color: 'var(--color-text-muted)',
            fontWeight: 500,
          }}
        >
          {formatViews(video.views)}
          {formatCreatedDate(video.createdAt)
            ? ` • ${formatCreatedDate(video.createdAt)}`
            : ''}
        </Typography>
      </Box>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// VideoWatchView — YouTube-style inline focus view.
// ---------------------------------------------------------------------------
// Replaces the old modal player. Layout:
//   • Desktop (md+): two columns — player + info on the left (~2/3),
//     "Related Videos" rail on the right (~1/3).
//   • Mobile / tablet: single column — player, info, then a vertical
//     "Related Videos" rail underneath.
//
// `otherVideos` is filtered upstream to exclude the active video and
// is already in display order. Selecting any entry calls `onPlay`,
// which swaps both the active video *and* the filter that produces
// `otherVideos`, so the rail naturally re-orders.

function VideoWatchView({
  video,
  otherVideos,
  onPlay,
  onBack,
}: {
  video: ProjectVideoItem;
  otherVideos: ProjectVideoItem[];
  onPlay: (video: ProjectVideoItem) => void;
  onBack: () => void;
}) {
  const hasNotes = Boolean(video.notesUrl);
  const handleDownloadNotes = () => {
    if (!video.notesUrl) return;
    logVideoNotesDownloaded(video.id);
    triggerDownload(video.notesUrl, buildNotesFilename(video));
  };

  return (
    <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }}>
      {/* Back to Videos pill — replaces the dashboard pill while a
          video is in focus, so the user has a clear escape hatch. */}
      <Box>
        <Button
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 999,
            px: 1.5,
            color: 'var(--color-text-strong)',
            borderColor: 'var(--color-border-strong)',
            '&:hover': {
              bgcolor: 'var(--color-surface-elevated)',
              borderColor: 'var(--color-border-strongest, var(--color-border-strong))',
            },
          }}
        >
          Back to Videos
        </Button>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(0, 1fr)' },
          gap: { xs: 2.5, md: 3 },
          alignItems: 'flex-start',
        }}
      >
        {/* ── Main column: player + info inside a single Card ────── */}
        {/* `key={video.id}` forces React to unmount and remount the
            underlying media element when the active video changes —
            required for iframe src swaps to take. */}
        <Card
          key={video.id}
          sx={{
            minWidth: 0,
            bgcolor: 'var(--color-surface)',
            color: 'var(--color-text)',
            borderRadius: 2,
            overflow: 'hidden',
            border: '1.5px solid var(--color-border-strong)',
            boxShadow: 'var(--color-shadow-card)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ bgcolor: '#000' }}>
            <VideoPlayer video={video} />
          </Box>

          <Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-start"
            sx={{
              flexWrap: 'wrap',
              p: { xs: 1.5, sm: 2 },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: 'var(--color-text-strong)',
                  lineHeight: 1.3,
                  letterSpacing: '-0.01em',
                }}
              >
                {video.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: 'var(--color-text-muted)',
                  fontWeight: 500,
                }}
              >
                {formatViews(video.views)}
                {formatCreatedDate(video.createdAt)
                  ? ` • Uploaded ${formatCreatedDate(video.createdAt)}`
                  : ''}
              </Typography>
            </Box>

            {hasNotes && (
              <Button
                onClick={handleDownloadNotes}
                startIcon={<FileDownloadIcon />}
                variant="outlined"
                size="medium"
                aria-label={`Download notes for ${video.title}`}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 999,
                  color: 'var(--color-accent-strong)',
                  borderColor: 'var(--color-border-strong)',
                  bgcolor: 'var(--color-surface)',
                  '&:hover': {
                    bgcolor: 'var(--color-accent-soft)',
                    borderColor: 'var(--color-accent)',
                  },
                }}
              >
                Download Notes
              </Button>
            )}
          </Stack>
        </Card>

        {/* ── Side column / mobile rail: Related Videos ────────── */}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 800,
              color: 'var(--color-text-strong)',
              mb: 1,
              letterSpacing: '0.01em',
              textTransform: 'uppercase',
              fontSize: '0.78rem',
            }}
          >
            Related Videos
          </Typography>
          {otherVideos.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: 'var(--color-text-muted)', py: 1 }}
            >
              No other videos in this category.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {otherVideos.map((v) => (
                <RelatedVideoCard key={v.id} video={v} onPlay={onPlay} />
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function ProjectVideosPage() {
  const navigate = useNavigate();
  const [activeVideo, setActiveVideo] = useState<ProjectVideoItem | null>(null);
  const [filters, setFilters] = usePeFilters();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filterCount = countPeFilters(filters);
  const { chips: appliedFilterChips, clearAll: clearAllFilters } = useAppliedFilterChips(
    filters,
    setFilters,
  );
  const { search, setSearch, dismissDropdownKey, dropdownContent } = usePeSearchDropdown();

  const handlePlayVideo = (video: ProjectVideoItem) => {
    logVideoClicked(video.id);
    setActiveVideo(video);
  };
  // Default to the first tab. Switching tabs lazily fetches that
  // category's videos if the cache slot is empty.
  const [activeCategory, setActiveCategory] = useState<VideoCategoryId>(
    VIDEO_CATEGORIES[0].id,
  );

  // Per-tab cache. Each slot is independent so switching tabs doesn't
  // re-fetch a category we've already loaded, and an error in one
  // category doesn't poison the others.
  const [byCategory, setByCategory] = useState<Record<VideoCategoryId, CategoryState>>({
    resi: { ...EMPTY_STATE },
    upcoming: { ...EMPTY_STATE },
    commercial: { ...EMPTY_STATE },
    international: { ...EMPTY_STATE },
    marketing: { ...EMPTY_STATE },
    consumer: { ...EMPTY_STATE },
  });

  // Fetch every category on mount so the tab labels can show counts
  // immediately. Each request is independent — a slow/failed fetch in
  // one category doesn't block the others. We deliberately do this
  // up front (rather than lazily on tab switch) because the user
  // explicitly asked for counts next to every tab label; counts that
  // only appear after a tab has been visited would be confusing.
  useEffect(() => {
    let cancelled = false;

    VIDEO_CATEGORIES.forEach(({ id }) => {
      // Mock-only categories skip the network round trip entirely
      // and hydrate from the local fixture above. Same shape as the
      // live payload so downstream rendering is identical.
      if (MOCK_CATEGORIES.has(id)) {
        const mockItems = MOCK_VIDEOS_BY_CATEGORY[id] ?? [];
        setByCategory((prev) => ({
          ...prev,
          [id]: { items: mockItems, loading: false, error: null },
        }));
        return;
      }

      setByCategory((prev) => ({
        ...prev,
        [id]: { items: null, loading: true, error: null },
      }));

      projectEssentialApi
        .getProjectVideos(id)
        .then((res) => {
          if (cancelled) return;
          setByCategory((prev) => ({
            ...prev,
            [id]: { items: res.items, loading: false, error: null },
          }));
        })
        .catch((err) => {
          if (cancelled) return;
          setByCategory((prev) => ({
            ...prev,
            [id]: {
              items: [],
              loading: false,
              error: (err as Error)?.message || 'Failed to load videos',
            },
          }));
        });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const activeState = byCategory[activeCategory];

  // Page size depends on the viewport: 8 on desktop (matches the
  // 4-column grid for two full rows) and 4 on mobile (one full
  // single-column screen). `sm` is the breakpoint MUI uses for
  // "tablet and up" elsewhere in this product.
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const pageSize = isDesktop ? 8 : 4;

  const [page, setPage] = useState(1);

  // Always sort newest first so the rail mirrors the YouTube
  // subscription feed ordering. The backend already orders by
  // CreatedAt DESC, but we sort defensively so the UI stays
  // deterministic if that ever changes.
  // Universal PE search does not narrow this list on keyup.
  const filteredVideos = useMemo(() => {
    if (!activeState.items) return [];
    return [...activeState.items].sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [activeState.items]);

  // Per-tab counts so the chip numbers match what the user would see
  // after switching tabs.
  const tabCounts = useMemo(() => {
    const counts: Partial<Record<VideoCategoryId, number | null>> = {};
    for (const { id } of VIDEO_CATEGORIES) {
      const items = byCategory[id].items;
      counts[id] = items == null ? null : items.length;
    }
    return counts;
  }, [byCategory]);

  // Reset pagination when the result set changes (tab / page size).
  // Without this the user could end up on "page 3 of 1" after
  // narrowing the list.
  useEffect(() => {
    setPage(1);
  }, [activeCategory, pageSize]);

  // Clamp the requested page in case `filteredVideos` shrank between
  // renders (e.g. data refresh dropped rows). `pageCount` is at
  // least 1 so the Pagination component always has a valid count.
  const pageCount = Math.max(1, Math.ceil(filteredVideos.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * pageSize;
  const visibleVideos = filteredVideos.slice(pageStart, pageStart + pageSize);

  // While a video is in focus we hand off rendering to VideoWatchView.
  // The "Related Videos" rail draws from the *full* sorted category list
  // (not the paginated slice) so the user can hop to any peer in the
  // same category, and we filter out the active row. Selecting a row
  // there calls `setActiveVideo`, which re-runs this filter — so the
  // rail naturally re-orders / re-filters as playback moves on.
  if (activeVideo) {
    const otherVideos = filteredVideos.filter((v) => v.id !== activeVideo.id);
    return (
      <VideoWatchView
        video={activeVideo}
        otherVideos={otherVideos}
        onPlay={handlePlayVideo}
        onBack={() => setActiveVideo(null)}
      />
    );
  }

  return (
    <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }}>
      {/* Back to dashboard — matches the pill used on Highlights & eBook,
          EDM, Seminars and Project Calendar pages. */}
      <Box>
        <Button
          onClick={() => navigate('/project-essential')}
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 999,
            px: 1.5,
            color: 'var(--color-text-strong)',
            borderColor: 'var(--color-border-strong)',
            '&:hover': {
              bgcolor: 'var(--color-surface-elevated)',
              borderColor: 'var(--color-border-strongest, var(--color-border-strong))',
            },
          }}
        >
          Back to Dashboard
        </Button>
      </Box>

      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          color: 'var(--color-accent)',
          textAlign: 'center',
          letterSpacing: '0.01em',
          fontSize: { xs: '1.6rem', sm: '2rem', md: '2.25rem' },
        }}
      >
        Project Videos
      </Typography>

      <PageSearchBar
        value={search}
        onChange={setSearch}
        placeholder="Search projects, EDMs, eBooks and reports…"
        onSubmit={(query) => navigate(buildPeSearchPageHref(query, filters))}
        filterCount={filterCount}
        onOpenFilters={() => setFiltersOpen(true)}
        appliedFilters={appliedFilterChips}
        onClearAllFilters={clearAllFilters}
        dropdownContent={dropdownContent}
        dismissDropdownKey={dismissDropdownKey}
      />

      <ProjectNavButtons active="videos" />

      {/* Catalogue is intentionally parked — show a Coming Soon state
          instead of tabs / grid until the videos surface is ready. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          gap: 1.25,
          py: { xs: 8, sm: 10 },
          px: 2,
          borderRadius: 2.5,
          border: '1px solid var(--color-border)',
          bgcolor: 'var(--color-surface)',
          backgroundImage:
            'radial-gradient(ellipse at 50% 0%, var(--color-accent-soft) 0%, transparent 62%)',
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'var(--color-accent-soft)',
            border: '1px solid var(--color-accent)',
            color: 'var(--color-accent-strong)',
            mb: 0.5,
          }}
        >
          <PlayArrowRoundedIcon sx={{ fontSize: 32 }} />
        </Box>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            color: 'var(--color-text-strong)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontSize: { xs: '1.35rem', sm: '1.6rem' },
          }}
        >
          Coming Soon
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'var(--color-text-strong)',
            fontWeight: 600,
            maxWidth: 480,
            lineHeight: 1.55,
            fontSize: { xs: '1rem', sm: '1.1rem' },
          }}
        >
          Watch. Discover. Share.
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'var(--color-text-muted)',
            maxWidth: 480,
            lineHeight: 1.55,
            fontSize: { xs: '0.95rem', sm: '1rem' },
          }}
        >
          Podcasts, project videos and market insights — ready to share with your clients.
        </Typography>
      </Box>

      <ProjectFiltersDialog
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        value={filters}
        onApply={(next) => {
          navigate(buildPeSearchPageHref(search.trim(), next));
        }}
      />
    </Stack>
  );
}
